import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { gameAPI, registrationAPI, drinksAPI } from '../../utils/api';
import { toast } from 'react-toastify';
import { getTicketTypeLabel, getDrinkLabel, generatePDF, generateExcel } from '../../utils/gameExports';

const GamesTab = ({ 
  games, 
  setGames, 
  selectedGame, 
  setSelectedGame, 
  showAddGame, 
  setShowAddGame, 
  loading, 
  setLoading,
  loadGames
}) => {
  const [gameForm, setGameForm] = useState({
    opponent: '',
    date: '',
    time: '',
    location: 'EFC Buseck', // Standard-Wert, wird nicht mehr angezeigt
    category: 'bundesliga',
    description: '',
    visible: true,
    registrationDeadline: ''
  });

  const navigate = useNavigate();
  const [showEditGame, setShowEditGame] = useState(false);
  const [editingGameId, setEditingGameId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('asc');
  const [selectedGames, setSelectedGames] = useState([]);
  const [viewMode, setViewMode] = useState('table');
  const [processingRegistrations, setProcessingRegistrations] = useState(new Set());
  const [drinkTypes, setDrinkTypes] = useState([]);

  useEffect(() => {
    loadDrinks();
  }, []);

  const loadDrinks = async () => {
    try {
      const response = await drinksAPI.getAllDrinks();
      if (response.success && response.drinks) {
        setDrinkTypes(response.drinks.map(drink => ({
          value: drink.value,
          label: drink.name
        })));
      }
    } catch (error) {
      console.error('Fehler beim Laden der Getränke:', error);
      // Fallback auf Standard-Getränke
      setDrinkTypes([
        { value: 'bier', label: 'Bier' },
        { value: 'aeppler', label: 'Äppler' },
        { value: 'cola', label: 'Cola' }
      ]);
    }
  };


  const handleApproveRegistration = async (registrationId, gameName) => {
    try {
      setProcessingRegistrations(prev => new Set([...prev, registrationId]));
      const response = await registrationAPI.updateRegistrationStatus(registrationId, 'confirmed');
      if (response.success) {
        toast.success(`Anmeldung für ${gameName} wurde genehmigt`);
        await loadGames();

        if (selectedGame) {
          const updatedGameResponse = await gameAPI.getGame(selectedGame.id);
          if (updatedGameResponse.success) {
            const updatedGame = {
              ...selectedGame,
              registrations: updatedGameResponse.game.registrations.map(reg => ({
                id: reg.id,
                userId: reg.user_id,
                name: reg.first_name && reg.last_name ?
                  `${reg.first_name} ${reg.last_name}` : reg.username || 'Unbekannt',
                email: reg.email || '',
                ticketType: reg.ticket_type,
                drink: reg.drink || '',
                message: reg.message || '',
                registeredAt: reg.created_at ? reg.created_at.split(' ')[0] : '',
                status: reg.status || 'confirmed'
              }))
            };
            setSelectedGame(updatedGame);
          }
        }
      }
    } catch (error) {
      console.error('Fehler beim Genehmigen der Anmeldung:', error);
      toast.error('Fehler beim Genehmigen der Anmeldung');
    } finally {
      setProcessingRegistrations(prev => {
        const newSet = new Set(prev);
        newSet.delete(registrationId);
        return newSet;
      });
    }
  };

  const handleRejectRegistration = async (registrationId, gameName) => {
    if (window.confirm('Möchten Sie diese Anmeldung wirklich ablehnen?')) {
      try {
        setProcessingRegistrations(prev => new Set([...prev, registrationId]));
        const response = await registrationAPI.updateRegistrationStatus(registrationId, 'cancelled');
        if (response.success) {
          toast.success(`Anmeldung für ${gameName} wurde abgelehnt`);
          await loadGames();

          if (selectedGame) {
            const updatedGameResponse = await gameAPI.getGame(selectedGame.id);
            if (updatedGameResponse.success) {
              const updatedGame = {
                ...selectedGame,
                registrations: updatedGameResponse.game.registrations.map(reg => ({
                  id: reg.id,
                  userId: reg.user_id,
                  name: reg.first_name && reg.last_name ?
                    `${reg.first_name} ${reg.last_name}` : reg.username || 'Unbekannt',
                  email: reg.email || '',
                  ticketType: reg.ticket_type,
                  drink: reg.drink || '',
                  message: reg.message || '',
                  registeredAt: reg.created_at ? reg.created_at.split(' ')[0] : '',
                  status: reg.status || 'confirmed'
                }))
              };
              setSelectedGame(updatedGame);
            }
          }
        }
      } catch (error) {
        console.error('Fehler beim Ablehnen der Anmeldung:', error);
        toast.error('Fehler beim Ablehnen der Anmeldung');
      } finally {
        setProcessingRegistrations(prev => {
          const newSet = new Set(prev);
          newSet.delete(registrationId);
          return newSet;
        });
      }
    }
  };

  const handleGameFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setGameForm({
      ...gameForm,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleAddGame = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const gameData = {
        opponent: gameForm.opponent,
        gameDate: gameForm.date,
        gameTime: gameForm.time,
        location: gameForm.location,
        category: gameForm.category,
        description: gameForm.description,
        visible: gameForm.visible,
        registrationDeadline: gameForm.registrationDeadline || null
      };

      const response = await gameAPI.createGame(gameData);
      if (response.success) {
        toast.success('Spiel wurde erfolgreich hinzugefügt!');
        setGameForm({
          opponent: '',
          date: '',
          time: '',
          location: 'EFC Buseck',
          category: 'bundesliga',
          description: '',
          visible: true,
          registrationDeadline: ''
        });
        setShowAddGame(false);
        await loadGames();
      }
    } catch (error) {
      console.error('Fehler beim Hinzufügen des Spiels:', error);
      toast.error(error.message || 'Fehler beim Hinzufügen des Spiels');
    } finally {
      setLoading(false);
    }
  };

  // Datum aus der API ('2026-08-29' oder ISO-String) in das Format des date-Inputs bringen
  const toDateInputValue = (value) => {
    if (!value) return '';
    const asString = String(value);
    if (/^\d{4}-\d{2}-\d{2}$/.test(asString)) return asString;
    const d = new Date(asString);
    if (isNaN(d.getTime())) return '';
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };

  const handleEditGame = (game) => {
    setEditingGameId(game.id);
    setGameForm({
      opponent: game.opponent || '',
      date: toDateInputValue(game.date),
      time: (game.time || '').slice(0, 5),
      location: game.location || 'EFC Buseck',
      category: game.category || 'bundesliga',
      description: game.description || '',
      visible: game.visible !== false,
      registrationDeadline: game.registrationDeadline
        ? String(game.registrationDeadline).slice(0, 16).replace(' ', 'T')
        : ''
    });
    setShowEditGame(true);
  };

  const handleUpdateGame = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await gameAPI.updateGame(editingGameId, {
        opponent: gameForm.opponent,
        gameDate: gameForm.date,
        gameTime: gameForm.time,
        location: gameForm.location,
        category: gameForm.category,
        description: gameForm.description,
        visible: gameForm.visible,
        registrationDeadline: gameForm.registrationDeadline || null
      });

      if (response.success) {
        toast.success('Spiel wurde aktualisiert!');
        setShowEditGame(false);
        setEditingGameId(null);
        await loadGames();
        if (selectedGame && selectedGame.id === editingGameId) {
          const refreshed = await gameAPI.getGame(editingGameId);
          if (refreshed.success) {
            setSelectedGame({ ...selectedGame, ...refreshed.game });
          }
        }
      }
    } catch (error) {
      console.error('Fehler beim Aktualisieren des Spiels:', error);
      toast.error(error.message || 'Fehler beim Aktualisieren des Spiels');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteGame = async (gameId) => {
    if (window.confirm('Möchten Sie dieses Spiel wirklich löschen?')) {
      try {
        const response = await gameAPI.deleteGame(gameId);
        if (response.success) {
          toast.success('Spiel wurde gelöscht!');
          if (selectedGame && selectedGame.id === gameId) {
            setSelectedGame(null);
          }
          await loadGames();
        }
      } catch (error) {
        console.error('Fehler beim Löschen des Spiels:', error);
        toast.error(error.message || 'Fehler beim Löschen des Spiels');
      }
    }
  };

  const getCategoryLabel = (category) => {
    const categories = {
      'bundesliga': 'Bundesliga',
      'champions-league': 'Champions League',
      'dfb-pokal': 'DFB-Pokal',
      'europa-league': 'Europa League'
    };
    return categories[category] || category;
  };

  const getFilteredAndSortedGames = () => {
    let filtered = games.filter(game => {
      const matchesSearch = game.opponent.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           game.location.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCategory = categoryFilter === 'all' || game.category === categoryFilter;
      return matchesSearch && matchesCategory;
    });

    return filtered.sort((a, b) => {
      let comparison = 0;

      switch (sortBy) {
        case 'date':
          comparison = new Date(a.date) - new Date(b.date);
          break;
        case 'opponent':
          comparison = a.opponent.localeCompare(b.opponent);
          break;
        case 'location':
          comparison = a.location.localeCompare(b.location);
          break;
        case 'registrations':
          comparison = (a.registrations?.length || 0) - (b.registrations?.length || 0);
          break;
        default:
          comparison = 0;
      }

      return sortOrder === 'asc' ? comparison : -comparison;
    });
  };

  const handleSelectGame = (gameId) => {
    setSelectedGames(prev => 
      prev.includes(gameId) 
        ? prev.filter(id => id !== gameId)
        : [...prev, gameId]
    );
  };

  const handleSelectAllGames = () => {
    const filteredGames = getFilteredAndSortedGames();
    if (selectedGames.length === filteredGames.length) {
      setSelectedGames([]);
    } else {
      setSelectedGames(filteredGames.map(game => game.id));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedGames.length === 0) return;

    if (window.confirm(`Möchten Sie wirklich ${selectedGames.length} Spiel(e) löschen?`)) {
      try {
        await Promise.all(selectedGames.map(gameId => gameAPI.deleteGame(gameId)));
        toast.success(`${selectedGames.length} Spiel(e) wurden gelöscht!`);
        setSelectedGames([]);
        await loadGames();
      } catch (error) {
        console.error('Fehler beim Löschen der Spiele:', error);
        toast.error('Fehler beim Löschen der Spiele');
      }
    }
  };

  // Ohne Teilnehmerbegrenzung sagt der Status jetzt, ob die Fahrt fuer
  // Mitglieder sichtbar und die Anmeldung noch offen ist.
  const getGameStatus = (game) => {
    const now = new Date();

    if (new Date(game.date) < now) {
      return { status: 'past', label: 'Vergangen', color: 'gray' };
    }
    if (game.visible === false) {
      return { status: 'past', label: 'Ausgeblendet', color: 'gray' };
    }
    if (game.registrationDeadline && new Date(game.registrationDeadline) < now) {
      return { status: 'almost-full', label: 'Anmeldung geschlossen', color: 'orange' };
    }
    return { status: 'open', label: 'Anmeldung offen', color: 'green' };
  };

  return (
    <div className="games-tab">
      {/* Controls */}
      <div className="admin-controls">
        <div className="admin-actions">
          <button
            onClick={() => setShowAddGame(true)}
            className="btn btn-primary"
          >
            + Neues Spiel hinzufügen
          </button>
          {selectedGames.length > 0 && (
            <button
              onClick={handleBulkDelete}
              className="btn btn-danger"
            >
              {selectedGames.length} Spiel(e) löschen
            </button>
          )}
        </div>

        {/* Filter */}
        <div className="admin-filters">
          <div className="filter-group">
            <input
              type="text"
              placeholder="Suche nach Gegner oder Ort..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>

          <div className="filter-group">
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="filter-select"
            >
              <option value="all">Alle Wettbewerbe</option>
              <option value="bundesliga">Bundesliga</option>
              <option value="champions-league">Champions League</option>
              <option value="dfb-pokal">DFB-Pokal</option>
              <option value="europa-league">Europa League</option>
            </select>
          </div>

          <div className="filter-group">
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="filter-select"
            >
              <option value="date">Nach Datum</option>
              <option value="opponent">Nach Gegner</option>
              <option value="location">Nach Ort</option>
              <option value="registrations">Nach Anmeldungen</option>
            </select>
            <button
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
              className="sort-order-btn"
              title={sortOrder === 'asc' ? 'Aufsteigend' : 'Absteigend'}
            >
              {sortOrder === 'asc' ? '↑' : '↓'}
            </button>
          </div>

          <div className="view-toggle">
            <button
              onClick={() => setViewMode('table')}
              className={`view-btn ${viewMode === 'table' ? 'active' : ''}`}
            >
              Tabelle
            </button>
            <button
              onClick={() => setViewMode('cards')}
              className={`view-btn ${viewMode === 'cards' ? 'active' : ''}`}
            >
              Karten
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="admin-content">
        {loading ? (
          <div className="loading"><p>Lade Spiele...</p></div>
        ) : (
          <>
            {/* Stats */}
            <div className="games-stats">
              <div className="stat-card"><h3>{games.length}</h3><p>Gesamt Spiele</p></div>
              <div className="stat-card"><h3>{games.filter(g => getGameStatus(g).status === 'open').length}</h3><p>Verfügbar</p></div>
              <div className="stat-card"><h3>{games.filter(g => getGameStatus(g).status === 'full').length}</h3><p>Ausgebucht</p></div>
              <div className="stat-card"><h3>{games.reduce((sum, g) => sum + (g.registrations?.length || 0), 0)}</h3><p>Gesamt Anmeldungen</p></div>
            </div>

            {/* Tabelle oder Kartenansicht */}
            {viewMode === 'table' ? (
              <div className="games-table-wrapper">
                <table className="games-table">
                  <thead>
                    <tr>
                      <th>
                        <input
                          type="checkbox"
                          checked={selectedGames.length === getFilteredAndSortedGames().length && getFilteredAndSortedGames().length > 0}
                          onChange={handleSelectAllGames}
                        />
                      </th>
                      <th>Status</th>
                      <th>Gegner</th>
                      <th>Datum & Zeit</th>
                      <th>Ort</th>
                      <th>Wettbewerb</th>
                      <th>Anmeldungen</th>
                      <th>Aktionen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {getFilteredAndSortedGames().map((game) => {
                      const status = getGameStatus(game);
                      return (
                        <tr key={game.id} className={selectedGames.includes(game.id) ? 'selected' : ''}>
                          <td>
                            <input
                              type="checkbox"
                              checked={selectedGames.includes(game.id)}
                              onChange={() => handleSelectGame(game.id)}
                            />
                          </td>
                          <td className="status-cell">
                            <span className={`status-badge status-${status.status}`}>
                              {status.label}
                            </span>
                          </td>
                          <td className="opponent-cell">
                            {game.opponent}
                          </td>
                          <td>
                            <div className="date-time">
                              <div className="date">{new Date(game.date).toLocaleDateString('de-DE')}</div>
                              <div className="time">{game.time}</div>
                            </div>
                          </td>
                          <td className="location-cell">{game.location}</td>
                          <td className="category-cell">
                            <span className={`category-badge ${game.category}`}>
                              {getCategoryLabel(game.category)}
                            </span>
                          </td>
                          <td className="registrations-cell">
                            <div className="registrations-info">
                              <div className="count">
                                {game.registrations ? game.registrations.length : 0}
                                <br />
                                <small style={{color: '#28a745', fontSize: '0.8em'}}>
                                  {game.registrations ? game.registrations.filter(r => r.status === 'confirmed').length : 0} bestätigt
                                </small>
                              </div>
                            </div>
                          </td>
                          <td className="actions-cell">
                            <div className="action-buttons">
                              <button
                                onClick={() => navigate(`/admin/spiel/${game.id}`)}
                                className="btn btn-sm btn-secondary"
                                title="Anmeldungen anzeigen"
                              >
                                👁️
                              </button>
                              <button
                                onClick={() => generatePDF(game, drinkTypes)}
                                className="btn btn-sm btn-primary"
                                disabled={!game.registrations || game.registrations.length === 0}
                                title="PDF Export"
                              >
                                📄
                              </button>
                              <button
                                onClick={() => generateExcel(game, drinkTypes)}
                                className="btn btn-sm btn-success"
                                disabled={!game.registrations || game.registrations.length === 0}
                                title="Excel Export"
                              >
                                📊
                              </button>
                              <button
                                onClick={() => handleEditGame(game)}
                                className="btn btn-sm btn-secondary"
                                title="Bearbeiten"
                              >
                                ✏️
                              </button>
                              <button
                                onClick={() => handleDeleteGame(game.id)}
                                className="btn btn-sm btn-danger"
                                title="Löschen"
                              >
                                🗑️
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="games-grid">
                {getFilteredAndSortedGames().map((game) => {
                  const status = getGameStatus(game);
                  return (
                    <div key={game.id} className={`game-card ${selectedGames.includes(game.id) ? 'selected' : ''}`}>
                      <div className="card-header">
                        <input
                          type="checkbox"
                          checked={selectedGames.includes(game.id)}
                          onChange={() => handleSelectGame(game.id)}
                          className="card-checkbox"
                        />
                        <span className={`status-badge ${status.status}`} style={{backgroundColor: status.color}}>
                          {status.label}
                        </span>
                      </div>
                      <div className="game-info">
                        <h3>{game.opponent}</h3>
                        <p className="game-date">
                          {new Date(game.date).toLocaleDateString('de-DE')} um {game.time}
                        </p>
                        <p className="game-location">📍 {game.location}</p>
                        <span className={`category-badge ${game.category}`}>
                          {getCategoryLabel(game.category)}
                        </span>
                        <div className="game-stats">
                          <div className="registrations-info">
                            <span className="count">
                              {game.registrations ? game.registrations.length : 0} Anmeldungen
                              <br />
                              <small style={{color: '#28a745'}}>
                                {game.registrations ? game.registrations.filter(r => r.status === 'confirmed').length : 0} bestätigt
                              </small>
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="game-actions">
                        <button
                          onClick={() => navigate(`/admin/spiel/${game.id}`)}
                          className="btn btn-secondary btn-sm"
                        >
                          Anmeldungen anzeigen
                        </button>
                        <button
                          onClick={() => generatePDF(game, drinkTypes)}
                          className="btn btn-primary btn-sm"
                          disabled={!game.registrations || game.registrations.length === 0}
                        >
                          📄 PDF
                        </button>
                        <button
                          onClick={() => generateExcel(game, drinkTypes)}
                          className="btn btn-success btn-sm"
                          disabled={!game.registrations || game.registrations.length === 0}
                        >
                          📊 Excel
                        </button>
                        <button
                          onClick={() => handleEditGame(game)}
                          className="btn btn-secondary btn-sm"
                        >
                          ✏️ Bearbeiten
                        </button>
                        <button
                          onClick={() => handleDeleteGame(game.id)}
                          className="btn btn-danger btn-sm"
                        >
                          Löschen
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Keine Spiele */}
            {getFilteredAndSortedGames().length === 0 && (
              <div className="empty-state">
                <p>
                  {searchTerm || categoryFilter !== 'all'
                    ? 'Keine Spiele gefunden, die den Filterkriterien entsprechen.'
                    : 'Noch keine Spiele vorhanden.'}
                </p>
                {(searchTerm || categoryFilter !== 'all') && (
                  <button
                    onClick={() => { setSearchTerm(''); setCategoryFilter('all'); }}
                    className="btn btn-secondary"
                  >
                    Filter zurücksetzen
                  </button>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Anmeldungsdetails Modal */}
      {/* Spiel hinzufügen Modal */}
      {showEditGame && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>Spiel bearbeiten</h2>
              <button
                onClick={() => { setShowEditGame(false); setEditingGameId(null); }}
                className="close-btn"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleUpdateGame} className="game-form">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="editOpponent">Gegner *</label>
                  <input
                    type="text"
                    id="editOpponent"
                    name="opponent"
                    value={gameForm.opponent}
                    onChange={handleGameFormChange}
                    required
                    placeholder="z.B. FC Bayern München"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="editCategory">Wettbewerb</label>
                  <select
                    id="editCategory"
                    name="category"
                    value={gameForm.category}
                    onChange={handleGameFormChange}
                  >
                    <option value="bundesliga">Bundesliga</option>
                    <option value="champions-league">Champions League</option>
                    <option value="dfb-pokal">DFB-Pokal</option>
                    <option value="europa-league">Europa League</option>
                    <option value="other">Sonstiges</option>
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="editDate">Datum *</label>
                  <input
                    type="date"
                    id="editDate"
                    name="date"
                    value={gameForm.date}
                    onChange={handleGameFormChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="editTime">Uhrzeit *</label>
                  <input
                    type="time"
                    id="editTime"
                    name="time"
                    value={gameForm.time}
                    onChange={handleGameFormChange}
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="editLocation">Ort</label>
                <input
                  type="text"
                  id="editLocation"
                  name="location"
                  value={gameForm.location}
                  onChange={handleGameFormChange}
                  placeholder="z.B. EFC Buseck oder Auswärts: 1. FSV Mainz 05"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="editDeadline">Anmeldung möglich bis</label>
                  <input
                    type="datetime-local"
                    id="editDeadline"
                    name="registrationDeadline"
                    value={gameForm.registrationDeadline}
                    onChange={handleGameFormChange}
                  />
                  <small>Leer lassen für keine Frist. Danach können sich Mitglieder nicht mehr an- oder abmelden.</small>
                </div>
                <div className="form-group">
                  <label>
                    <input
                      type="checkbox"
                      name="visible"
                      checked={gameForm.visible}
                      onChange={handleGameFormChange}
                    />
                    {' '}Für Mitglieder sichtbar
                  </label>
                  <small>Ausgeblendete Fahrten sieht nur der Vorstand.</small>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="editDescription">Beschreibung</label>
                <textarea
                  id="editDescription"
                  name="description"
                  value={gameForm.description}
                  onChange={handleGameFormChange}
                  rows="3"
                  placeholder="Zusätzliche Informationen zum Spiel..."
                />
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  onClick={() => { setShowEditGame(false); setEditingGameId(null); }}
                  className="btn btn-secondary"
                >
                  Abbrechen
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={loading}
                >
                  {loading ? 'Speichere...' : 'Änderungen speichern'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showAddGame && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>Neues Spiel hinzufügen</h2>
              <button
                onClick={() => setShowAddGame(false)}
                className="close-btn"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleAddGame} className="game-form">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="opponent">Gegner *</label>
                  <input
                    type="text"
                    id="opponent"
                    name="opponent"
                    value={gameForm.opponent}
                    onChange={handleGameFormChange}
                    required
                    placeholder="z.B. FC Bayern München"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="category">Wettbewerb</label>
                  <select
                    id="category"
                    name="category"
                    value={gameForm.category}
                    onChange={handleGameFormChange}
                  >
                    <option value="bundesliga">Bundesliga</option>
                    <option value="champions-league">Champions League</option>
                    <option value="dfb-pokal">DFB-Pokal</option>
                    <option value="europa-league">Europa League</option>
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="date">Datum *</label>
                  <input
                    type="date"
                    id="date"
                    name="date"
                    value={gameForm.date}
                    onChange={handleGameFormChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="time">Uhrzeit *</label>
                  <input
                    type="time"
                    id="time"
                    name="time"
                    value={gameForm.time}
                    onChange={handleGameFormChange}
                    required
                  />
                </div>
              </div>

              {/* Location-Feld entfernt - wird automatisch auf "EFC Buseck" gesetzt */}

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="addDeadline">Anmeldung möglich bis</label>
                  <input
                    type="datetime-local"
                    id="addDeadline"
                    name="registrationDeadline"
                    value={gameForm.registrationDeadline}
                    onChange={handleGameFormChange}
                  />
                  <small>Leer lassen für keine Frist. Danach können sich Mitglieder nicht mehr an- oder abmelden.</small>
                </div>
                <div className="form-group">
                  <label>
                    <input
                      type="checkbox"
                      name="visible"
                      checked={gameForm.visible}
                      onChange={handleGameFormChange}
                    />
                    {' '}Für Mitglieder sichtbar
                  </label>
                  <small>Ausgeblendete Fahrten sieht nur der Vorstand.</small>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="description">Beschreibung</label>
                <textarea
                  id="description"
                  name="description"
                  value={gameForm.description}
                  onChange={handleGameFormChange}
                  rows="3"
                  placeholder="Zusätzliche Informationen zum Spiel..."
                />
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  onClick={() => setShowAddGame(false)}
                  className="btn btn-secondary"
                >
                  Abbrechen
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={loading}
                >
                  {loading ? 'Erstelle...' : 'Spiel hinzufügen'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default GamesTab;
